<template>
  <bm-control v-bind="$attrs" :read-only-value="value">
    <template #default="{ dataQa }">
      <v-menu
        ref="menuComponentRef"
        v-model="showColourPicker"
        max-width="300"
        nudge-right="40"
        :return-value="selectedColour"
        :close-on-content-click="false"
        @update:return-value="updateSelectedColour"
      >
        <template #activator="{ on }">
          <v-text-field
            v-model="selectedColourText"
            v-bind="$attrs"
            class="bm-text"
            prepend-icon="colorize"
            color="brand02"
            readonly
            :clearable="clearable"
            :placeholder="placeholderVal"
            :data-qa="dataQa"
            :data-component="dataComponent"
            :label="undefined"
            v-on="on"
          >
            <template v-if="value" #prepend-inner>
              <v-chip pill small :color="value" />
            </template>
          </v-text-field>
        </template>
        <v-card :data-qa="dataQa">
          <v-color-picker
            v-if="showColourPicker"
            v-model="selectedColour"
            v-bind="$attrs"
            :swatches="swatchColours"
            :mode="'hexa'"
            show-swatches
          />
          <v-card-actions>
            <v-container pa-0 fluid>
              <v-row justify="space-between">
                <v-col cols="auto" class="py-0">
                  <bm-btn
                    icon
                    x-large
                    button-type="ghost"
                    @click="showColourPicker = false"
                  >
                    <v-icon>
                      {{ $vuetify.icons.values[icons.cancel] }}
                    </v-icon>
                  </bm-btn>
                </v-col>
                <v-col cols="auto" class="py-0">
                  <bm-btn
                    icon
                    x-large
                    button-type="ghost"
                    @click="menuComponentRef?.save(selectedColour)"
                  >
                    <v-icon>
                      {{ $vuetify.icons.values[icons.accept] }}
                    </v-icon>
                  </bm-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-menu>
    </template>
    <template #readOnlyValue>
      <template v-if="value">
        <v-chip pill small :color="value" />
        {{ selectedColourText || value }}
      </template>
    </template>
  </bm-control>
</template>

<script lang="ts">
import {
  getSwatchColours,
  getSwatchNameColourPairs,
} from "@/app/ui/colourUtils";
import BmControl from "@/components/controls/input/BmControl.vue";
import messages, { useI18n } from "@/plugins/i18n";
import { computed, defineComponent, ref, watch } from "vue";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";
import { useThemedIcons } from "@/plugins/vuetify";

// Couldn't hack into Vuetify's own definition, so I'll define it manually for now
// Mimicks the defined object: https://vuetifyjs.com/en/api/v-color-picker/#events
// https://github.com/vuetifyjs/vuetify/issues/12117
interface VColorPickerColor {
  alpha: number;
  hex: string;
  hexa: string;
  hsla: {
    h: number;
    s: number;
    l: number;
    a: number;
  };
  hsva: {
    h: number;
    s: number;
    v: number;
    a: number;
  };
  hue: number;
  rgba: {
    r: number;
    g: number;
    b: number;
    a: number;
  };
}

/**
 * Colour Picking component designed to return hexa colour strings that the user selects.
 *
 * Notes
 *  - selectedColour is the bound value into the VColorPicker control, but we only update the the bound prop's value when the VMenu is accepted
 *    clicking off doesn't update the value. This is by intent, as constantly updating the prop value makes the component very laggy
 *
 *  - The pre-defined swatches are listed in our translations, in a CSV [name],[colour] format
 *
 *  - Bug with vuetify dealing incorrectly with null. Provide default value to avoid https://github.com/vuetifyjs/vuetify/issues/12117
 *
 */
export default defineComponent({
  name: "BmColourPicker",
  components: { BmControl },
  model: {
    event: "input",
    prop: "value",
  },
  props: {
    value: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    swatches: {
      type: String,
      required: false,
      default: messages.swatch_colours,
      validator: (value: unknown): boolean => {
        if (typeof value !== "string") {
          return false;
        }

        const parsedSwatches = getSwatchColours(value);
        return parsedSwatches.every((swatchGroup) =>
          // Check each group matches our expectation
          swatchGroup.every(
            ([colour]) =>
              // We have a colour in a string format
              !!colour && typeof colour === "string"
          )
        );
      },
    },
  },
  setup(props, context) {
    const { $m } = useI18n();
    const showColourPicker = ref(false);
    const selectedColour = ref<VColorPickerColor | string | null>("");
    const placeholderVal = computed(() => props.placeholder ?? " ");
    const { icons } = useThemedIcons();

    // Refs
    const menuComponentRef = ref<{
      save: (selectedColour: VColorPickerColor | string | null) => void;
    } | null>(null);

    // TypeGuard to verify if the returned value from VColorPicker is their object or hexa string
    const isVuetifyColourObject = (
      colourPickerReturnValue: any
    ): colourPickerReturnValue is VColorPickerColor => {
      return (colourPickerReturnValue as VColorPickerColor)?.hexa !== undefined;
    };

    const swatchNameColourPairs = getSwatchNameColourPairs(props.swatches);

    // Converts the returned VColourPicker value into our hexa string
    const convertVColorToHexaString = (
      colour: VColorPickerColor | string | null
    ) => (isVuetifyColourObject(colour) ? colour.hexa : colour);

    const selectedColourText = computed({
      // Our text value should should be our pre-defined colour names, or just the hexa value
      get: () => {
        // Grab just the hexa colour value
        const hexaTextValue = convertVColorToHexaString(selectedColour.value);

        // Return either the specific colour's name, or just the colour's value
        return hexaTextValue
          ? swatchNameColourPairs
              .find((nameColourPair) => nameColourPair.includes(hexaTextValue))
              ?.split(",")[0] ?? hexaTextValue
          : hexaTextValue;
      },
      // If we clear our text field, update the value and push it up
      set: (value) => {
        selectedColour.value = value;
        context.emit("input", value ?? "");
      },
    });

    // On menuComponent.save, set our new values and update our prop
    const updateSelectedColour = (
      newColour: VColorPickerColor | string | null
    ) => {
      const newColourHexaString = convertVColorToHexaString(newColour);

      selectedColour.value = newColourHexaString;
      selectedColourText.value = newColourHexaString;
      context.emit("input", selectedColour.value ?? "");
    };

    // If our root value changes, make sure our internal values updates
    watch(
      () => props.value,
      () => {
        if (selectedColour.value !== props.value) {
          selectedColour.value = props.value;
          selectedColourText.value = props.value;
        }
      },
      { immediate: true }
    );

    return {
      showColourPicker,
      selectedColour,
      selectedColourText,
      placeholderVal,
      swatchColours: getSwatchColours(props.swatches),
      swatchNameColourPairs,
      dataComponent: dataComponentAttrs.bmColourPicker,
      icons,

      // Funcs
      updateSelectedColour,

      // refs
      menuComponentRef,
    };
  },
});
</script>
